import { Navigate, Route, Routes } from 'react-router-dom';
import { AUTH_ROUTES } from '@/modules/auth/constants';
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPasswordPage,
} from '@/modules/auth/pages';
import { AUTOMATIONS_ROUTES, AutomationsPage } from '@/modules/automations';
import { BILLING_ROUTES, LicensePage } from '@/modules/billing';
import { BOARD_ROUTES, BoardPage } from '@/modules/board';
import { BOT_ROUTES, BotConfigPage } from '@/modules/bot';
import {
  CAMPAIGNS_ROUTES,
  CampaignPage,
  CampaignsPage,
  ContactsImportPage,
} from '@/modules/campaigns';
import { CASHBACK_ROUTES, CashbackEntriesPage, CashbackRulesPage } from '@/modules/cashback';
import { CATALOG_ROUTES, CategoriesPage, ServiceFormPage, ServicesPage } from '@/modules/catalog';
import {
  COMMISSIONS_ROUTES,
  CommissionReportPage,
  CommissionRulesPage,
} from '@/modules/commissions';
import { COMPANY_ROUTES, CompanyPage, GoalsPage, UnitsPage } from '@/modules/company';
import { CONTACT_LISTS_ROUTES, ContactListsPage } from '@/modules/contact-lists';
import { CONVERSATIONS_ROUTES, ConversationsPage } from '@/modules/conversations';
import { COUPONS_ROUTES, CouponsPage } from '@/modules/coupons';
import { CUSTOMERS_ROUTES, CustomerFormPage, CustomersPage } from '@/modules/customers';
import {
  BillsPage,
  FINANCE_ROUTES,
  FinanceCategoriesPage,
  PayablesPage,
  ReceivablesPage,
} from '@/modules/finance';
import { LANDING_ROUTES } from '@/modules/landing/api';
import { LandingConfigPage } from '@/modules/landing/pages/landing-config-page';
import { SalonLandingPage as PublicSalonPage } from '@/modules/landing/pages/salon-page';
import { ConversionReportPage, FunnelBoardPage, LEADS_ROUTES } from '@/modules/leads';
import {
  AppointmentsReportPage,
  AuditPage,
  CashFlowPage,
  IncomeStatementPage,
  MANAGEMENT_ROUTES,
  RevenueReportPage,
} from '@/modules/management';
import { MARKETING_ROUTES, SalonLandingPage } from '@/modules/marketing';
import { NOTIFICATIONS_ROUTES, NotificationsPage } from '@/modules/notifications';
import { ONBOARDING_ROUTES, OnboardingPage } from '@/modules/onboarding';
import { PROFILES_ROUTES } from '@/modules/profiles/constants';
import { ProfileFormPage, ProfilesPage } from '@/modules/profiles/pages';
// "Contas a receber" (financeiro) e "Recebimentos" (cartao) sao telas
// diferentes com nomes parecidos - o apelido evita a confusao aqui.
import {
  ReceivablesPage as CardReceivablesPage,
  MachinesPage,
  RECEIVABLES_ROUTES,
} from '@/modules/receivables';
import {
  SAAS_ROUTES,
  SaasAuditPage,
  SaasBillsPage,
  SaasBusinessXrayPage,
  SaasCashFlowPage,
  SaasFaturamentoPage,
  SaasFinanceCategoriesPage,
  SaasIncomeStatementPage,
  SaasPayablesPage,
  SaasReceivablesPage,
  SaasSettingsPage,
  SaasTemplatesPage,
  SaasUsersPage,
  SaasVendaPage,
  SubscriberPage,
  SubscribersPage,
} from '@/modules/saas';
import {
  AgendaPage,
  AppointmentFormPage,
  SCHEDULE_ROUTES,
  ServiceDeskPage,
} from '@/modules/schedule';
import { AppearancePage, PermissionsPage, ProfilePage } from '@/modules/settings';
import { SETTINGS_ROUTES } from '@/modules/settings/constants';
import { SIGNUP_ROUTES, SignupWizardPage, WaitlistPage } from '@/modules/signup';
import { SUPPORT_ROUTES, SupportPage } from '@/modules/support';
import { USERS_ROUTES } from '@/modules/users/constants';
import { UserFormPage, UsersPage } from '@/modules/users/pages';
import { AppLayout, AuthLayout } from '../layouts';
import { HomeRoute } from './home-route';
import { NotFoundPage } from './not-found-page';
import { RequirePermission } from './permission-route';
import { ProtectedRoute, PublicOnlyRoute } from './protected-route';
import { RequireSaasAdmin } from './saas-route';

/** Arvore de rotas da aplicacao. */
export function AppRouter() {
  return (
    <Routes>
      {/* Landing publica: sem sessao e sem layout do app. */}
      <Route path={MARKETING_ROUTES.SALON_LANDING} element={<SalonLandingPage />} />
      {/* Pagina do salao, onde o cliente agenda sozinho. */}
      <Route path={LANDING_ROUTES.PUBLIC_PATTERN} element={<PublicSalonPage />} />

      {/* Wizard de cadastro do CTA - publico ao comecar, e o mesmo endereco
          que retoma o cadastro de quem ja logou e ainda nao terminou. O
          passo atual vai na URL (/cadastro/dados, /cadastro/horarios...) -
          a propria pagina corrige a URL se nao bater com onde a pessoa
          esta de verdade. */}
      <Route path={SIGNUP_ROUTES.WIZARD} element={<SignupWizardPage />} />
      <Route path={`${SIGNUP_ROUTES.WIZARD}/:step`} element={<SignupWizardPage />} />

      {/* Fila de espera: sem o layout do app (sem menu) - so essa tela
          mesmo, ate o time liberar o acesso na mao. */}
      <Route
        path={SIGNUP_ROUTES.WAITLIST}
        element={
          <ProtectedRoute>
            <WaitlistPage />
          </ProtectedRoute>
        }
      />

      {/* Rotas publicas */}
      <Route
        element={
          <PublicOnlyRoute>
            <AuthLayout />
          </PublicOnlyRoute>
        }
      >
        <Route path={AUTH_ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={AUTH_ROUTES.REGISTER} element={<RegisterPage />} />
        <Route path={AUTH_ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={AUTH_ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      </Route>

      {/* Rotas privadas */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path={AUTH_ROUTES.DASHBOARD} element={<HomeRoute />} />

        {/* Consultar exige READ; criar e editar exigem WRITE. */}
        <Route element={<RequirePermission module="users" />}>
          <Route path={USERS_ROUTES.LIST} element={<UsersPage />} />
        </Route>
        <Route element={<RequirePermission module="users" level="WRITE" />}>
          <Route path={USERS_ROUTES.NEW} element={<UserFormPage />} />
          <Route path={USERS_ROUTES.DETAIL_PATTERN} element={<UserFormPage />} />
        </Route>

        <Route element={<RequirePermission module="profiles" />}>
          <Route path={PROFILES_ROUTES.LIST} element={<ProfilesPage />} />
        </Route>
        <Route element={<RequirePermission module="profiles" level="WRITE" />}>
          <Route path={PROFILES_ROUTES.NEW} element={<ProfileFormPage />} />
          <Route path={PROFILES_ROUTES.DETAIL_PATTERN} element={<ProfileFormPage />} />
        </Route>

        {/* Agenda e atendimento (blocos 3 e 4) */}
        <Route element={<RequirePermission module="schedule" />}>
          <Route path={SCHEDULE_ROUTES.AGENDA} element={<AgendaPage />} />
          <Route path={SCHEDULE_ROUTES.DETAIL_PATTERN} element={<AppointmentFormPage />} />
        </Route>
        <Route element={<RequirePermission module="schedule" level="WRITE" />}>
          <Route path={SCHEDULE_ROUTES.NEW} element={<AppointmentFormPage />} />
        </Route>
        <Route element={<RequirePermission module="appointments" />}>
          <Route path={SCHEDULE_ROUTES.SERVICE_DESK} element={<ServiceDeskPage />} />
          <Route path={CONVERSATIONS_ROUTES.LIST} element={<ConversationsPage />} />
          <Route path={LEADS_ROUTES.BOARD} element={<FunnelBoardPage />} />
        </Route>

        {/* Cadastros do bloco 2 */}
        <Route element={<RequirePermission module="customers" />}>
          <Route path={CUSTOMERS_ROUTES.LIST} element={<CustomersPage />} />
        </Route>
        <Route element={<RequirePermission module="customers" level="WRITE" />}>
          <Route path={CUSTOMERS_ROUTES.NEW} element={<CustomerFormPage />} />
          <Route path={CUSTOMERS_ROUTES.DETAIL_PATTERN} element={<CustomerFormPage />} />
        </Route>

        <Route element={<RequirePermission module="services" />}>
          <Route path={CATALOG_ROUTES.SERVICES} element={<ServicesPage />} />
          <Route path={CATALOG_ROUTES.CATEGORIES} element={<CategoriesPage />} />
        </Route>
        <Route element={<RequirePermission module="services" level="WRITE" />}>
          <Route path={CATALOG_ROUTES.SERVICE_NEW} element={<ServiceFormPage />} />
          <Route path={CATALOG_ROUTES.SERVICE_DETAIL_PATTERN} element={<ServiceFormPage />} />
        </Route>

        {/* Financeiro */}
        <Route element={<RequirePermission module="finance" />}>
          <Route path={FINANCE_ROUTES.BILLS} element={<BillsPage />} />
          <Route path={FINANCE_ROUTES.PAYABLES} element={<PayablesPage />} />
          <Route path={FINANCE_ROUTES.RECEIVABLES} element={<ReceivablesPage />} />
          <Route path={FINANCE_ROUTES.CATEGORIES} element={<FinanceCategoriesPage />} />
          <Route path={CASHBACK_ROUTES.RULES} element={<CashbackRulesPage />} />
          <Route path={CASHBACK_ROUTES.ENTRIES} element={<CashbackEntriesPage />} />
          <Route path={COUPONS_ROUTES.LIST} element={<CouponsPage />} />
          <Route path={COMMISSIONS_ROUTES.RULES} element={<CommissionRulesPage />} />
          <Route path={RECEIVABLES_ROUTES.LIST} element={<CardReceivablesPage />} />
          <Route path={RECEIVABLES_ROUTES.MACHINES} element={<MachinesPage />} />
        </Route>

        {/* Guia de configuracao: aberto a quem entra, e nao so ao dono. */}
        <Route path={ONBOARDING_ROUTES.PROGRESS} element={<OnboardingPage />} />

        {/* Notificacoes: toda pessoa logada tem as suas. */}
        <Route path={NOTIFICATIONS_ROUTES.LIST} element={<NotificationsPage />} />

        {/* Marketing */}
        <Route element={<RequirePermission module="customers" />}>
          <Route path={CAMPAIGNS_ROUTES.LIST} element={<CampaignsPage />} />
          <Route path={CAMPAIGNS_ROUTES.DETAIL} element={<CampaignPage />} />
          <Route path={CAMPAIGNS_ROUTES.IMPORT} element={<ContactsImportPage />} />
          <Route path={CONTACT_LISTS_ROUTES.LIST} element={<ContactListsPage />} />
        </Route>
        <Route element={<RequirePermission module="settings" />}>
          <Route path={BOT_ROUTES.CONFIG} element={<BotConfigPage />} />
          <Route path={AUTOMATIONS_ROUTES.LIST} element={<AutomationsPage />} />
        </Route>

        {/* Gestao (bloco 5) */}
        <Route element={<RequirePermission module="reports" />}>
          <Route path={LEADS_ROUTES.CONVERSION} element={<ConversionReportPage />} />
          <Route
            path={MANAGEMENT_ROUTES.APPOINTMENTS_REPORT}
            element={<AppointmentsReportPage />}
          />
          <Route path={MANAGEMENT_ROUTES.REVENUE_REPORT} element={<RevenueReportPage />} />
          {/* A apuracao e relatorio; so as regras pedem permissao financeira. */}
          <Route path={COMMISSIONS_ROUTES.REPORT} element={<CommissionReportPage />} />
          <Route path={MANAGEMENT_ROUTES.CASH_FLOW} element={<CashFlowPage />} />
          <Route path={MANAGEMENT_ROUTES.INCOME_STATEMENT} element={<IncomeStatementPage />} />
          <Route path={MANAGEMENT_ROUTES.AUDIT} element={<AuditPage />} />
        </Route>

        <Route element={<RequirePermission module="settings" />}>
          <Route path={COMPANY_ROUTES.COMPANY} element={<CompanyPage />} />
          <Route path={COMPANY_ROUTES.UNITS} element={<UnitsPage />} />
          <Route path={COMPANY_ROUTES.GOALS} element={<GoalsPage />} />
          <Route path={LANDING_ROUTES.CONFIG} element={<LandingConfigPage />} />
          <Route
            path={COMPANY_ROUTES.WHATSAPP}
            element={<Navigate to={BILLING_ROUTES.LICENSE} replace />}
          />
          <Route path={BILLING_ROUTES.LICENSE} element={<LicensePage />} />
          <Route path={`${BILLING_ROUTES.LICENSE}/:tab`} element={<LicensePage />} />
          <Route path={SUPPORT_ROUTES.TICKETS} element={<SupportPage />} />
        </Route>

        {/* Administracao do SaaS: fora do sistema de perfis, que e por empresa. */}
        <Route element={<RequireSaasAdmin />}>
          <Route path={SAAS_ROUTES.SUBSCRIBERS} element={<SubscribersPage />} />
          <Route path={SUPPORT_ROUTES.SAAS_TICKETS} element={<SupportPage doSaas />} />
          <Route path={BOARD_ROUTES.BOARD} element={<BoardPage />} />
          <Route path={`${BOARD_ROUTES.BOARD}/:tab`} element={<BoardPage />} />
          <Route path={SAAS_ROUTES.SUBSCRIBER_DETAIL} element={<SubscriberPage />} />
          <Route path={SAAS_ROUTES.AUDIT} element={<SaasAuditPage />} />
          <Route path={SAAS_ROUTES.SETTINGS} element={<SaasSettingsPage />} />
          <Route path={SAAS_ROUTES.SETTINGS_ASSISTANT} element={<SaasTemplatesPage />} />
          <Route path={SAAS_ROUTES.USERS} element={<SaasUsersPage />} />
          <Route path={SAAS_ROUTES.VENDA} element={<SaasVendaPage />} />
          <Route path={SAAS_ROUTES.GESTAO_FATURAMENTO} element={<SaasFaturamentoPage />} />
          <Route path={SAAS_ROUTES.GESTAO_DFC} element={<SaasCashFlowPage />} />
          <Route path={SAAS_ROUTES.GESTAO_DRE} element={<SaasIncomeStatementPage />} />
          <Route path={SAAS_ROUTES.GESTAO_RAIO_X} element={<SaasBusinessXrayPage />} />
          <Route path={SAAS_ROUTES.FINANCEIRO_CATEGORIAS} element={<SaasFinanceCategoriesPage />} />
          <Route path={SAAS_ROUTES.FINANCEIRO_CONTAS} element={<SaasBillsPage />} />
          <Route path={SAAS_ROUTES.FINANCEIRO_A_PAGAR} element={<SaasPayablesPage />} />
          <Route path={SAAS_ROUTES.FINANCEIRO_A_RECEBER} element={<SaasReceivablesPage />} />
        </Route>

        <Route path={SETTINGS_ROUTES.PROFILE} element={<ProfilePage />} />
        <Route path={SETTINGS_ROUTES.APPEARANCE} element={<AppearancePage />} />
        <Route path={SETTINGS_ROUTES.PERMISSIONS} element={<PermissionsPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export { NotFoundPage } from './not-found-page';
export { RequirePermission } from './permission-route';
export { ProtectedRoute, PublicOnlyRoute } from './protected-route';
